/** Card cancellation confirmation, shared by the admin and employee drawers. */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import type { InternalRefetchQueriesInclude } from "@apollo/client";
import { ConfirmDialog } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { CreditCardNotAcceptIcon } from "@hugeicons-pro/core-stroke-rounded";
import { DENY_CARD } from "graphql/card";

type CancelCardDialogProps = {
  open: boolean;
  cardId: string | null;
  /** Close the dialog without cancelling. */
  onClose: () => void;
  /** Called after the card is successfully canceled. */
  onCanceled: () => void;
  /** Reports the mutation in flight so the parent drawer can gate its close paths. */
  onBusyChange?: (busy: boolean) => void;
  /** Lists each route refreshes itself; there is no query both of them own. */
  refetchQueries?: InternalRefetchQueriesInclude;
};

const CancelCardDialog: React.FC<CancelCardDialogProps> = ({
  open,
  cardId,
  onClose,
  onCanceled,
  onBusyChange,
  refetchQueries,
}) => {
  const { t } = useTranslation();

  const [denyCard, { loading: canceling }] = useMutation(DENY_CARD, {
    refetchQueries,
    onCompleted: () => {
      toast.success(t("Card canceled"));
      onClose();
      onCanceled();
    },
    onError: () => {
      toast.error(t("Please contact support"));
    },
  });

  const handleConfirm = () => {
    if (!cardId) return;
    denyCard({ variables: { id: cardId } }).catch(() => {});
  };

  // The parent drawer gates its close paths on this; the cleanup clears it so an
  // unmount mid-flight can't leave the drawer stuck.
  useEffect(() => {
    onBusyChange?.(canceling);
    return () => onBusyChange?.(false);
  }, [canceling, onBusyChange]);

  return (
    <ConfirmDialog
      open={open}
      // The DS only swallows a backdrop click while busy; the close X and
      // Escape still fire, so the gate is ours to hold.
      onCancel={() => {
        if (canceling) return;
        onClose();
      }}
      onConfirm={handleConfirm}
      title={t("Cancel Card")}
      icon={
        <HugeiconsIcon
          icon={CreditCardNotAcceptIcon}
          size={20}
          color="currentColor"
        />
      }
      confirmLabel={t("Yes, cancel card")}
      cancelLabel={t("No")}
      busy={canceling}
      message={t(
        "This will permanently cancel the card and it can no longer be used, are you sure you want to continue?",
      )}
    />
  );
};

export default CancelCardDialog;
